import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AutocompleteFromUri, AUTOCOMPLETEFROMURI_COLUMN_URI, AutocompleteOption, AutocompleteWithGivenOptions, AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA, CHECKBOX_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI, CODEBAR_SCANNER_URI, ConditionPath, DATEPICKER_COLUMN_URI, DATETIMEPICKER_COLUMN_URI, DType, DTYPEAUTOCOMPLETE_URI, DTYPEBOOLEAN_URI, DTYPECHIPLIST_URI, DTypeCol, DTYPECUSTOM_URI, DTYPEDATETIME_URI, DTYPEDECORATION_URI, DTYPEEMBEDDED_URI, DTypeFamilies, DTypeFamily, DTYPEFILE_URI, DTYPENUMBER_URI, DTYPESELECT_URI, DTYPETEXT_URI, DTYPETRIGGER_URI, FORMATTEDTEXT_COLUMN_URI, INTEGER_COLUMN_URI, LANGUAGETEXTAREA_COLUMN_URI, LANGUAGETEXT_COLUMN_URI, NextLocator, Operator, RADIO_COLUMN_URI, RATING_COLUMN_URI, REAL_COLUMN_URI, SECTION_COLUMN_URI, SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI, SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SelectFromUri, SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, SelectOption, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI, SELECTSERVICE_COLUMN_URI, SelectWithGivenOptions, SELECTWITHGIVENOPTIONS_COLUMN_URI, SUBSECTION_COLUMN_URI, TEXTAREA_COLUMN_URI, TEXT_COLUMN_URI, TIMEPICKER_COLUMN_URI, TIMERANGESCONVERTER_COLUMN_URI, TRIGGER_COLUMN_URI, TRISTATE_COLUMN_URI } from '@grupo-constant/next-dtype/types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { formatISO } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom, map, startWith, Subject, takeUntil } from 'rxjs';
import { DTypeService } from '../../dtype.service';

import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { LocalizePipe } from '../../../pipes/standalone/localize.pipe';
import { TimepickerAutofillComponent } from '../../../components/timepicker-autofill/timepicker-autofill.component';
import { FuseScrollbarDirective } from '../../../../../@fuse/directives/scrollbar/scrollbar.directive';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';
import { LocalizeService } from '../../../pipes/localize.service';

@Component({
    selector: 'dtype-condition',
    templateUrl: './dtype-condition.dialog.html',
    styles: [
        /* language=SCSS */
        `
            .dtype-condition-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    imports: [TranslocoModule, MatIconModule, MatFormFieldModule, MatSelectModule, LocalizePipe, MatSlideToggleModule, MatRadioModule, MatDatepickerModule, TimepickerAutofillComponent, MatChipsModule, FormsModule, MatAutocompleteModule, ReactiveFormsModule, FuseScrollbarDirective],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class DtypeConditionDialogComponent implements OnInit, OnDestroy {

    readonly checkboxUri = CHECKBOX_COLUMN_URI;
    readonly tristateUri = TRISTATE_COLUMN_URI;
    readonly radioUri = RADIO_COLUMN_URI;
    readonly datepickerUri = DATEPICKER_COLUMN_URI;
    readonly timepickerUri = TIMEPICKER_COLUMN_URI;
    readonly datetimepickerUri = DATETIMEPICKER_COLUMN_URI;
    readonly timerangesUri = TIMERANGESCONVERTER_COLUMN_URI;
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];

    formGroup = new FormGroup({
        field: new FormControl('', Validators.required),
        fieldType: new FormControl<string>('', Validators.required),
        operator: new FormControl<Operator>(undefined, Validators.required),
        operand: new FormControl(),
        path: new FormControl(),
        isReadOnly: new FormControl<boolean>(false)
    });

    chiplistControl = new FormControl<string>('');
    filteredOptions: SelectOption[];

    fields: DTypeCol[];
    currentField: DTypeCol;

    operators: SelectOption[];
    families: DTypeFamily[] = [];
    commonConditions = [
        Operator.EMPTY, Operator.NOT_EMPTY,
        Operator.HIDDEN, Operator.NOT_HIDDEN,
        Operator.MANDATORY, Operator.NOT_MANDATORY,
        Operator.READONLY, Operator.NOT_READONLY
    ];
    conditionsByComponent = new Map<string, Map<string, Operator[]>>();

    private unsubscribeAll = new Subject<void>();

    public data: {
        field: DTypeCol;
        fields: DTypeCol[];
        operatorsOptions: SelectOption[];
        condition: ConditionPath;
        conditions: ConditionPath[];
        dtype: DType;
        locator: NextLocator;
    } = inject(MAT_DIALOG_DATA);
    private dialogsService = inject(DialogsService);
    private dtypeService = inject(DTypeService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private localize = inject(LocalizeService);
    public matDialogRef = inject(MatDialogRef<DtypeConditionDialogComponent>);
    private translocoService = inject(TranslocoService);

    get field() {
        return this.formGroup.get('field').value;
    }

    get fieldType() {
        return this.formGroup.get('fieldType').value;
    }

    get operator() {
        return this.formGroup.get('operator').value;
    }

    get operand() {
        return this.formGroup.get('operand').value;
    }

    get isReadOnly() {
        return this.formGroup.get('isReadOnly').value;
    }

    set field(field: string) {
        this.formGroup.get('field').setValue(field);
    }

    set fieldType(fieldType: string) {
        this.formGroup.get('fieldType').setValue(fieldType);
    }

    set operator(operator: Operator) {
        this.formGroup.get('operator').setValue(operator);
    }

    set operand(operand: any) {
        this.formGroup.get('operand').setValue(operand);
    }

    set isReadOnly(isReadOnly: boolean) {
        this.formGroup.get('isReadOnly').setValue(isReadOnly);
    }

    ngOnInit() {
        this.fields = this.data.fields.filter(col => col.name !== this.data.field.name && col.type !== TRIGGER_COLUMN_URI && col.type !== FORMATTEDTEXT_COLUMN_URI
            && col.type !== SECTION_COLUMN_URI && col.type !== SUBSECTION_COLUMN_URI);

        this.formGroup.get('field').valueChanges.pipe(
            takeUntil(this.unsubscribeAll)
        ).subscribe((field) => {
            this.onChangeField(field);
        });

        this.formGroup.get('operator').valueChanges.pipe(
            takeUntil(this.unsubscribeAll)
        ).subscribe((operator) => {
            this.onChangeOperator(operator);
        });

        this.chiplistControl.valueChanges
            .pipe(
                takeUntil(this.unsubscribeAll),
                startWith(null),
                map((value: string | null) => {
                    if (this.currentFieldIsSelect()) {
                        return value ? this.filterOptions(value) : (this.currentField as SelectWithGivenOptions).option
                            ?? (this.currentField as SelectFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : [];
                    } else if (this.currentFieldIsAutocomplete()) {
                        return value ? this.filterOptions(value) : (this.currentField as AutocompleteWithGivenOptions).option
                            ?? (this.currentField as AutocompleteFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : [];
                    }
                })
            ).subscribe(options => this.filteredOptions = options ?? []);

        this.dtypeService.retrieveDTypeBuilderCollection().subscribe((families) => {
            this.fillConditionsMap(families);
            if (this.data.condition) {
                this.field = this.data.condition.field;
                this.fieldType = this.data.condition.fieldType;
                this.operator = this.data.condition.operator;
                this.operand = this.data.condition.operand;
                this.isReadOnly = this.data.condition.isReadOnly;
                this.formGroup.get('path').setValue(this.data.condition.path);
                if (this.isReadOnly) {
                    this.formGroup.disable();
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    async save() {
        const confirmDialog = this.openConfirmDialog(this.translocoService.translate('saveConditionConfirm'));
        const confirm = await firstValueFrom(confirmDialog.afterClosed());
        if (confirm === 'confirmed') {
            this.formGroup.markAllAsTouched();
            if (this.formGroup.valid) {
                const condition: ConditionPath = this.formGroup.getRawValue();
                if (this.data.conditions.find(cond => cond.path !== condition.path && cond.field === condition.field
                    && cond.operand === condition.operand && cond.operator === condition.operator)) {
                        this.dialogsService.openErrorDialog({
                            message: this.translocoService.translate('conditionAlreadyExists')
                        });
                    return;
                }
                this.matDialogRef.close(condition);
            } else {
                this.dialogsService.openErrorDialog({
                    message: this.translocoService.translate('errorsInForm')
                });
            }
        }
    }

    addToChiplist(event: MatChipInputEvent) {
        const value = event.value || '';
        const list: string[] = this.operand ?? [];
        list.push(value);
        this.operand = list;
        this.chiplistControl.setValue('');
    }

    removeFromChiplist(index: number) {
        const list: string[] = this.operand;
        list.splice(index, 1);
        this.operand = list;
    }

    selectedAutocomplete(event: MatAutocompleteSelectedEvent) {
        const value = cloneDeep(event.option.value);
        if (value) {
            const list: string[] = this.operand ?? [];
            list.push(value);
            this.operand = list;
        }
        this.chiplistControl.setValue(undefined);
    }

    getChipCaption(chip: string) {
        const options = this.filteredOptions;
        const option = options.find(opt => opt.code === chip);
        if (option) {
            return this.localize.transform(option.value);
        }
        return '';
    }

    currentFieldIsText() { // TODO languagetext no se puede hacer equals
        return [
            TEXT_COLUMN_URI, TEXTAREA_COLUMN_URI,
            LANGUAGETEXT_COLUMN_URI, LANGUAGETEXTAREA_COLUMN_URI, CODEBAR_SCANNER_URI
        ].includes(this.fieldType);
    }

    currentFieldIsNumber() {
        return [INTEGER_COLUMN_URI, REAL_COLUMN_URI, RATING_COLUMN_URI].includes(this.fieldType);
    }

    currentFieldIsSelect() {
        return [
            SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI,
            SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI,
            SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI,
            SELECTSERVICE_COLUMN_URI, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI
        ].includes(this.fieldType);
    }

    currentFieldIsAutocomplete() {
        return [
            AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AUTOCOMPLETEFROMURI_COLUMN_URI,
            AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA
        ].includes(this.fieldType);
    }

    currentFieldIsTrigger() {
        return TRIGGER_COLUMN_URI === this.fieldType;
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    dateChange(event: any) {
        this.operand = event.value ? formatISO(Date.parse(String(event.value)), { representation: 'date' }) : '';
    }

    private fillConditionsMap(families: DTypeFamilies) {
        for (const [uri, family] of Object.entries(families)) {
            for (const component of family.component) {
                let conditions: Operator[] = [];
                if (uri === DTYPETRIGGER_URI) {
                    conditions.push(Operator.ONE_OF, Operator.NOT_ONE_OF);
                } else {
                    conditions = [...this.commonConditions];
                    switch (uri) {
                        case DTYPEAUTOCOMPLETE_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF);
                            break;
                        case DTYPEBOOLEAN_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS);
                            break;
                        case DTYPECHIPLIST_URI:
                            break;
                        case DTYPECUSTOM_URI:
                            break;
                        case DTYPEDATETIME_URI:
                            switch (component.uri) {
                                case DATEPICKER_COLUMN_URI:
                                case TIMEPICKER_COLUMN_URI:
                                    conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.GREATERTHAN, Operator.LESSTHAN);
                                    break;
                            }
                            break;
                        case DTYPEDECORATION_URI:
                            break;
                        case DTYPEEMBEDDED_URI:
                            break;
                        case DTYPEFILE_URI:
                            break;
                        case DTYPENUMBER_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.GREATERTHAN, Operator.LESSTHAN);
                            break;
                        case DTYPESELECT_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF);
                            break;
                        case DTYPETEXT_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF);
                            break;
                    }
                }
                if (!this.conditionsByComponent.get(family.family)) {
                    this.conditionsByComponent.set(family.family, new Map<string, Operator[]>());
                }
                this.conditionsByComponent.get(family.family).set(component.uri, conditions);
            }
            this.families.push(family);
        }
    }

    private onChangeField(field: string) {
        this.operator = undefined;
        this.currentField = this.fields.find(col => col.name === field);
        if (this.currentField) {
            this.fieldType = this.currentField.type;
            const family = this.families.find(fam => fam.component.some(comp => comp.uri === this.currentField.type));
            if (family) {
                const conditions: string[] = this.conditionsByComponent.get(family.family).get(this.currentField.type);
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code));
            }

            if (this.currentFieldIsSelect() || this.currentFieldIsAutocomplete()) {
                this.chiplistControl.setValue(null);
            }
        }
    }

    private onChangeOperator(operator: Operator) {
        if ([
            Operator.EMPTY, Operator.NOT_EMPTY,
            Operator.HIDDEN, Operator.NOT_HIDDEN,
            Operator.MANDATORY, Operator.NOT_MANDATORY,
            Operator.READONLY, Operator.NOT_READONLY
        ].includes(operator)) {
            this.formGroup.get('operand').clearValidators();
            this.operand = undefined;
        } else {
            this.formGroup.get('operand').addValidators(Validators.required);
            if (this.fieldType === this.checkboxUri) {
                this.operand = false;
            } else {
                this.operand = undefined;
            }
        }
    }

    private filterOptions(value: any): SelectOption[] | AutocompleteOption[] {
        if (typeof value === 'string') {
            const filterValue = value.toLowerCase();
            if (this.currentFieldIsSelect()) {
                const options = (this.currentField as SelectWithGivenOptions).option
                    ?? (this.currentField as SelectFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : [];
                return options.filter(option =>
                    this.removeAccents(this.localize.transform(option.value)).toLowerCase()
                        .includes(this.removeAccents(filterValue)));
            } else if (this.currentFieldIsAutocomplete()) {
                const options = (this.currentField as AutocompleteWithGivenOptions).option
                    ?? (this.currentField as AutocompleteFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : [];
                return options.filter(option =>
                    this.removeAccents(this.localize.transform(option.value)).toLowerCase()
                        .includes(this.removeAccents(filterValue)));
            }
        }
    }

    private removeAccents(str: string) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.translocoService.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });
    }
}
